/*
  Licensed under the Apache License, Version 2.0 (the "License"); you may not use
  this file except in compliance with the License. You may obtain a copy of the
  License at

      https://www.apache.org/licenses/LICENSE-2.0

  Unless required by applicable law or agreed to in writing, software distributed
  under the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR
  CONDITIONS OF ANY KIND, either express or implied. See the License for the
  specific language governing permissions and limitations under the License.
*/

import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import makeStyles from "@mui/styles/makeStyles";
import Typography from "@mui/material/Typography";
import { noop } from "lodash";
import React from "react";
import { useTranslation } from "react-i18next";

import { ErrorMessageProps } from "./model";

const useStyles = makeStyles(theme => ({
  root: {
    zIndex: 10010
  }
}));

export function ErrorMessages(props: ErrorMessageProps) {
  const {
    autoHideDuration = 5000,
    className,
    disableAutoHide,
    errors,
    onClose = noop
  } = props;

  const classes = useStyles();
  const { t } = useTranslation();

  // backend errors surface with titles like "ExposedError.DocumentError"
  const friendlyTitle = (title: string): string =>
    title.startsWith("ExposedError") ? t("Error") : title;

  return (
    <Snackbar
      className={`${classes.root} ${className} marapp-qa-errormessages`}
      open={!!errors?.length}
      autoHideDuration={disableAutoHide ? null : autoHideDuration}
      onClose={onClose}
      anchorOrigin={{
        vertical: "top",
        horizontal: "center"
      }}
    >
      <Alert onClose={onClose} variant="filled" severity="error" sx={{alignItems: "center"}}>
        {errors &&
          errors.map &&
          errors.map((error, index) => (
            <Typography key={index}>
              {error.title ? `${friendlyTitle(error.title)}: ` : ``}
              {/* backend details are full English sentences; ones with a matching
                  i18n key get translated, the rest pass through unchanged.
                  A detail carrying `params` is a key with {{placeholders}},
                  which i18next fills in whether or not the key is translated */}
              {error.detail && t(error.detail, error.params)}
              {error.source && (
                <>
                  <br />
                  <code>
                    {error.source.pointer} {error.source.body}
                    {/* enable this in a debug mode? */}
                    {/* {' '}{Object.entries(error.source.parameter || {}).map(([k,v]) => (
                      `${k}=${v}`
                    ))
                  } */}
                  </code>
                </>
              )}
            </Typography>
          ))}
      </Alert>
    </Snackbar>
  );
}
